import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  AI_REVIEW_ALLOWED_ACTIONS_OPTIONS,
  AI_REVIEW_MODE_OPTIONS,
  AI_REVIEW_STEP_TYPE_OPTIONS,
} from 'src/constants/ai-review/constants'
import { AiDocumentReviewMode } from 'src/constants/ai-document-review/enums'
import type {
  AiDocumentReviewConfig,
  AiDocumentReviewConfigFormValues,
} from 'src/interfaces/aiDocumentReviewConfig'

interface Props {
  /** Edit locks the row's identity — the document it points at cannot be repointed. */
  isEdit: boolean
  /** `null` creates; a config edits it. Used to derive the threshold lock from saved data. */
  config: AiDocumentReviewConfig | null
}

const hintCss = tw`mt-1 text-shades-500`

const AiReviewConfigFieldsComponent = ({ isEdit, config }: Props) => {
  const { values } = useFormikContext<AiDocumentReviewConfigFormValues>()

  // Shadow mode applies nothing to the step, so the allowed-actions choice has no effect yet.
  const isShadow = values.mode !== AiDocumentReviewMode.Active

  // The backend cannot clear a threshold once set — only replace or delete-and-recreate the row.
  // Deriving this from the saved config (not form values) means un-ticking the checkbox client
  // side can never produce a silent no-op PUT that drops the field.
  const thresholdLockedOn =
    config !== null &&
    config.autoApproveMinSlpRate !== undefined &&
    config.autoApproveMinSlpRate !== null

  return (
    <div css={tw`flex flex-col gap-4`}>
      <div>
        <FormikSelect<AiDocumentReviewConfigFormValues>
          name="stepType"
          label="Step type *"
          placeholder="Select a step type"
          options={AI_REVIEW_STEP_TYPE_OPTIONS}
          disabled={isEdit}
          inputProps={{ 'data-testid': 'ai-review-config-step-type-select' }}
        />
        <Typography variant="body2" css={hintCss}>
          {isEdit
            ? 'The document a row points at cannot be changed — delete the row and create a new one.'
            : 'The collect-data step this configuration applies to.'}
        </Typography>
      </div>

      <div>
        <FormikTextField<AiDocumentReviewConfigFormValues>
          name="label"
          label="Step label"
          placeholder="Leave empty to cover every step of this type"
          disabled={isEdit}
          data-testid="ai-review-config-label-input"
        />
        <Typography variant="body2" css={hintCss}>
          Matches the collect-data step label. A labelled step falls back to the unlabelled row, so
          a step type can be configured once instead of per label.
        </Typography>
      </div>

      <div>
        <FormikCheckbox<AiDocumentReviewConfigFormValues>
          name="enabled"
          label="Review this document with AI"
          inputProps={{ 'data-testid': 'ai-review-config-enabled-checkbox' }}
        />
        <Typography variant="body2" css={hintCss}>
          On by default. Clearing this opts this one document out — the step goes through the manual
          queue untouched.
        </Typography>
      </div>

      <div>
        <FormikSelect<AiDocumentReviewConfigFormValues>
          name="mode"
          label="Mode"
          options={AI_REVIEW_MODE_OPTIONS}
          disabled={!values.enabled}
          inputProps={{ 'data-testid': 'ai-review-config-mode-select' }}
        />
        <Typography variant="body2" css={hintCss}>
          Shadow records the decision without touching the step. Active lets it act on the step.
        </Typography>
      </div>

      <div>
        <FormikSelect<AiDocumentReviewConfigFormValues>
          name="allowedActions"
          label="Allowed actions"
          options={AI_REVIEW_ALLOWED_ACTIONS_OPTIONS}
          disabled={!values.enabled || isShadow}
          inputProps={{ 'data-testid': 'ai-review-config-allowed-actions-select' }}
        />
        <Typography variant="body2" css={hintCss}>
          Which decisions Active mode may apply to the step. A decision left out is still recorded —
          the step just waits for a person. Shadow mode applies nothing, so this only matters once
          the mode is Active.
        </Typography>
      </div>

      <div>
        <FormikCheckbox<AiDocumentReviewConfigFormValues>
          name="hasAutoApproveMinSlpRate"
          label="Set an explicit approval threshold"
          disabled={!values.enabled || thresholdLockedOn}
          inputProps={{ 'data-testid': 'ai-review-config-has-threshold-checkbox' }}
        />
        <Typography variant="body2" css={hintCss}>
          Minimum confidence score (0–100) a submission must reach before the decision can be
          Approve.
          {thresholdLockedOn
            ? ' A threshold cannot be cleared through this API — delete the row and recreate it to go back to the default.'
            : null}
        </Typography>
        {values.hasAutoApproveMinSlpRate ? (
          <div css={tw`mt-3`}>
            <FormikTextField<AiDocumentReviewConfigFormValues>
              name="autoApproveMinSlpRate"
              label="Threshold"
              type="number"
              disabled={!values.enabled}
              data-testid="ai-review-config-threshold-input"
            />
          </div>
        ) : null}
      </div>

      <div>
        <FormikTextArea<AiDocumentReviewConfigFormValues>
          name="instructions"
          label="Partner instructions"
          disabled={!values.enabled}
          fullWidth
          data-testid="ai-review-config-instructions-input"
        />
        <Typography variant="body2" css={hintCss}>
          Extra guidance for this partner's document, on top of the standard review instructions.
        </Typography>
      </div>

      <FormikCheckbox<AiDocumentReviewConfigFormValues>
        name="useInternalReviewInstructions"
        label="Use internal review instructions"
        disabled={!values.enabled}
        inputProps={{ 'data-testid': 'ai-review-config-internal-instructions-checkbox' }}
      />

      <div>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Checks
        </Typography>
        <div css={tw`flex flex-col gap-2`}>
          <FormikCheckbox<AiDocumentReviewConfigFormValues>
            name="identityMatchRequired"
            label="Identity match required"
            disabled={!values.enabled}
            inputProps={{ 'data-testid': 'ai-review-config-identity-checkbox' }}
          />
          <FormikCheckbox<AiDocumentReviewConfigFormValues>
            name="vehicleMatchRequired"
            label="Vehicle match required"
            disabled={!values.enabled}
            inputProps={{ 'data-testid': 'ai-review-config-vehicle-checkbox' }}
          />
          <FormikCheckbox<AiDocumentReviewConfigFormValues>
            name="expirationCheckRequired"
            label="Expiration check required"
            disabled={!values.enabled}
            inputProps={{ 'data-testid': 'ai-review-config-expiration-checkbox' }}
          />
        </div>
      </div>
    </div>
  )
}

export const AiReviewConfigFields = memo(AiReviewConfigFieldsComponent)
